/**
 * When the About panel shows itself, and what counts as having an About page at all.
 *
 * Pure and in the domain layer for the same reasons the theme and the sidebar are: what a
 * stored preference comes to, and whether an unprompted panel is welcome on this visit,
 * are decisions about the preference rather than about the DOM. The UI module is left
 * with a dialog, a `localStorage` key and a `fetch`.
 */

/** Where the dismissal is kept between visits. */
export const ABOUT_STORAGE_KEY = '3dpreview:about';

/**
 * The value written once the panel has been sent away.
 *
 * Spelled out rather than stored as `'true'`, so a look at the stored value says what it
 * means. Only this one word counts; anything else reads as never dismissed.
 */
const DISMISSED = 'dismissed';

/**
 * What the panel shows when there is no page to show.
 *
 * Markdown rather than a bare string, so that the empty case and the ordinary one go
 * through exactly the same parser and stylesheet. There is one rendering path, and the
 * fallback is not a special case of it.
 */
export const ABOUT_FALLBACK = '# Welcome to 3DPreview.eu';

/** Where the deployed page is looked for, relative to the page that fetches it. */
export const ABOUT_FILE = 'about.md';

/**
 * Read back a stored dismissal, falling back to not having been dismissed.
 *
 * Takes `string | null` because that is what `localStorage.getItem` gives. Anything
 * unrecognised reads as absent: a value written by an older version, or edited by hand,
 * must not be able to hide a panel that has never been seen.
 */
export function parseAboutDismissed(value: string | null | undefined): boolean {
  return value === DISMISSED;
}

/** What to store for a given state. */
export function aboutStorageValue(dismissed: boolean): string {
  return dismissed ? DISMISSED : 'unseen';
}

/**
 * Whether to put the panel up without being asked.
 *
 * Two conditions, and they answer different questions. **An empty library** means this
 * visitor has nothing to look at, so the space the panel would cover is space nothing
 * else is using; somebody who came back to a model they saved last week is here to see
 * that model, and a welcome over the top of it is an obstacle. **Never dismissed** means
 * they have not already said they are done with it — sending it away is a decision that
 * has to stick, or the button in the header would be the only way to get a page that no
 * longer interrupts.
 */
export function shouldOfferAbout(dismissed: boolean, modelCount: number): boolean {
  return !dismissed && modelCount === 0;
}

/**
 * Decide whether what came back from the server is an About page.
 *
 * The interesting case is not a 404 — the caller can see that for itself. It is a server
 * configured to answer unknown paths with `index.html`, which is a very common way to
 * host a single-page application and which answers **200 with a copy of this very page**.
 * Rendering that would show the viewer's own markup as prose, which looks far more like a
 * bug than a missing file does.
 *
 * So the content type is checked, and the body is sniffed as well: a server may hand back
 * its fallback as `text/plain`, and a document is recognisable whatever it is labelled.
 */
export function readAboutMarkdown(
  contentType: string | null,
  body: string,
): string | undefined {
  if (contentType !== null && /^\s*text\/html\b/i.test(contentType)) return undefined;

  const start = body.trimStart().slice(0, 200).toLowerCase();
  if (start.startsWith('<!doctype html') || start.startsWith('<html')) return undefined;

  // An empty file is a deployer who has not written the page yet, which is the same
  // situation as not having deployed one.
  return body.trim() === '' ? undefined : body;
}
